import { useState, useRef, type KeyboardEvent } from "react";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Checkbox } from "../ui/checkbox";

interface MfaFormProps {
  onSubmit: (code: string, trustDevice: boolean) => void | Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string | null;
  codeLength?: number;
  showTrustDevice?: boolean;
  trustDeviceLabel?: string;
}

export function MfaForm({
  onSubmit,
  onCancel,
  isLoading = false,
  error,
  codeLength = 6,
  showTrustDevice = false,
  trustDeviceLabel = "Trust this device for 30 days",
}: MfaFormProps) {
  const [digits, setDigits] = useState<string[]>(Array(codeLength).fill(""));
  const [trustDevice, setTrustDevice] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);

    if (value && index < codeLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const code = newDigits.join("");
    if (code.length === codeLength && !newDigits.includes("")) {
      onSubmit(code, trustDevice);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, codeLength);
    const newDigits = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);
    if (pasted.length === codeLength) {
      onSubmit(pasted, trustDevice);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Two-Factor Authentication
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Enter the {codeLength}-digit code from your authenticator app
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-danger-subtle p-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex justify-center gap-2">
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            disabled={isLoading}
            // Filled well rather than an outlined box; the focus ring is the
            // only edge it gets.
            className="h-12 w-12 rounded-md bg-sunken text-center text-xl font-bold text-slate-900 transition-colors focus:outline-none focus-fill disabled:opacity-50 dark:text-white"
          />
        ))}
      </div>

      {showTrustDevice && (
        <label className="flex items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none">
          <Checkbox
            checked={trustDevice}
            onCheckedChange={(checked) => setTrustDevice(checked === true)}
            disabled={isLoading}
          />
          {trustDeviceLabel}
        </label>
      )}

      <div className="flex gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          className="flex-1"
          disabled={isLoading || digits.join("").length < codeLength}
          onClick={() => onSubmit(digits.join(""), trustDevice)}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" /> Verifying...
            </>
          ) : (
            "Verify"
          )}
        </Button>
      </div>
    </div>
  );
}
